import { useState, useCallback } from 'react';
import { Copy, Check } from 'lucide-react';
import { cn } from '../../lib/utils.js';

const TERMINAL_LANGS = new Set(['terminal', 'shell', 'bash', 'sh', 'zsh', 'console']);

interface CodeBlockProps {
  code: string;
  language?: string;
  showLineNumbers?: boolean;
}

export function CodeBlock({ code, language, showLineNumbers }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);
  const isTerminal = TERMINAL_LANGS.has(language?.toLowerCase() || '');
  const showLines = showLineNumbers ?? code.split('\n').length > 5;

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }, [code]);

  const lines = code.split('\n');
  // Remove trailing empty line
  if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();

  return (
    <div className={cn(
      'group relative rounded-lg text-xs overflow-hidden',
      isTerminal
        ? 'bg-gray-900 text-green-300'
        : 'bg-gray-900 text-gray-100',
    )}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-gray-800/50 text-[10px] text-gray-400">
        <span>{language || 'code'}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 rounded px-1.5 py-0.5 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-gray-700"
        >
          {copied ? (
            <><Check className="h-3 w-3 text-green-400" /> Kopyalandı</>
          ) : (
            <><Copy className="h-3 w-3" /> Kopyala</>
          )}
        </button>
      </div>
      {/* Code */}
      <div className="overflow-x-auto p-3">
        <table className="border-collapse">
          <tbody>
            {lines.map((line, i) => (
              <tr key={i}>
                {showLines && (
                  <td className="select-none pr-3 text-right text-gray-600 align-top" style={{ minWidth: '2rem' }}>
                    {i + 1}
                  </td>
                )}
                <td className="whitespace-pre font-mono">{line || ' '}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
